import logger from './src/utils/logger.js';
import { getEnvVars } from './src/public-env-vars.js';

// CRITICAL: Initialize benefits API IMMEDIATELY for backward compatibility
if (!window.benefits) {
  window.benefits = {};
}

// IMMEDIATELY provide the init function - this MUST be the first thing that runs
window.benefits.init = function(token) {
  logger.log('benefits.init() called with token');

  // SECURITY: Store token in private variable, not on global object
  _secureAuthToken = token;

  // Always add to queue regardless of ready state for safety
  if (!this._initQueue) this._initQueue = [];
  this._initQueue.push(token);

  // Token will be passed to iframe for secure validation

  if (this._isReady) {
    // System is ready, update widgets immediately
    this._updateAllWidgets(token);
  }

  return Promise.resolve();
};

// Add essential support methods immediately
window.benefits._updateAllWidgets = function(token) {
  if (this._widgetInstances) {
    this._widgetInstances.forEach(widget => {
      if (widget && typeof widget.setAuthToken === 'function') {
        widget.setAuthToken(token);
      }
    });
  }
};

logger.log('BenefitsWidget loaded - benefits.init is available');

const WidgetPerformance = {
  metrics: new Map(),

  startTiming: function(label) {
    this.metrics.set(label, performance.now());
  },

  endTiming: function(label) {
    const start = this.metrics.get(label);
    if (start) {
      const duration = performance.now() - start;
      this.metrics.delete(label);
      return duration;
    }
    return null;
  },

  logMetrics: function() {
    logger.log('Widget Performance Metrics:', Object.fromEntries(this.metrics));
  }
};

// SECURITY: Private token storage - not accessible from global scope
let _secureAuthToken = null;
const _authorizedWidgets = new WeakSet(); // Track authorized widget instances

// Define the full benefits API with guaranteed init function
// Store the existing init function to preserve it
const existingInit = window.benefits.init;

Object.assign(window.benefits, {
  // Widget instances tracking (no token storage here)
  _widgetInstances: window.benefits._widgetInstances || new Set(),
  _initQueue: window.benefits._initQueue || [], // Queue for early init calls
  _isReady: false,
  _isSecureBenefitsAPI: true, // Security marker to verify legitimate API

  // CRITICAL: Preserve the existing init function that was set immediately
  init: existingInit,

  // Internal method to update all widgets
  _updateAllWidgets: function(token) {
    this._widgetInstances.forEach(widget => {
      if (widget && typeof widget.setAuthToken === 'function') {
        widget.setAuthToken(token);
      }
    });
  },

  // Internal method to mark system as ready and process queue
  _setReady: function() {
    this._isReady = true;

    // Process any queued init calls
    if (this._initQueue.length > 0) {
      const latestToken = this._initQueue[this._initQueue.length - 1];
      _secureAuthToken = latestToken;

      // Token will be passed to iframe for secure validation

      this._updateAllWidgets(latestToken);
      this._initQueue = []; // Clear queue
      logger.log('Processed queued benefits.init() call');
    }
  },

  // SECURITY: Secure token access - only for authorized widget instances
  _getSecureToken: function(widgetInstance) {
    // Verify the caller is an authorized widget instance
    if (!_authorizedWidgets.has(widgetInstance)) {
      logger.warn('Unauthorized token access attempt blocked');
      return null;
    }
    return _secureAuthToken;
  },

  // Register a widget instance for token updates
  _registerWidget: function(widget) {
    this._widgetInstances.add(widget);

    // SECURITY: Authorize this widget instance for secure token access
    _authorizedWidgets.add(widget);

    // If we already have a secure token, set it on the new widget
    if (_secureAuthToken && typeof widget.setAuthToken === 'function') {
      widget.setAuthToken(_secureAuthToken);
    }
  },

  // Unregister a widget instance
  _unregisterWidget: function(widget) {
    this._widgetInstances.delete(widget);

    // SECURITY: Remove authorization for this widget instance
    _authorizedWidgets.delete(widget);
  },

  defineCustomElements: function() {
    // This function is called by existing vendors but is no longer needed
    // The declarative widgets are automatically initialized by initDeclarativeWidgets()
    logger.log('benefits.defineCustomElements() called - declarative widgets already auto-initialized');
    return Promise.resolve();
  }
});

// SECURITY: Log usage for monitoring (production-ready)
logger.log(' SecureBenefitsWidget loaded');

class SecureBenefitsWidget {
  constructor(containerId, config = {}) {
    // Start performance timing
    WidgetPerformance.startTiming('widget-initialization');

    this.containerId = containerId;
    this.config = config;
    this.iframe = null;
    this.isReady = false;
    this.authToken = null;
    this.loadTimeout = null;
    this.retryCount = 0;
    this.maxRetries = 3;
    this.loadTimeoutMs = 10000; // 10 second timeout for the initial iframe navigation
    // Separate, longer budget for waiting on WIDGET_READY once the iframe has loaded - the
    // widget's own JS bundle (CSS + ESM/nomodule scripts + Stencil component chunks) needs
    // more time than the outer navigation, especially on slow connections.
    this.readyTimeoutMs = 30000;
    this.iframeLoadCompleted = false;
    this._pendingSearch = null;
    this._wasReady = false;
    this._missedHealthChecks = 0;
    this._widgetId = 'bw-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 9);
    this.createdAt = Date.now();
    this.callbacks = {
      onResult: null,
      onReady: null,
      onError: null, 
      onTokenRefresh: null,
      onSubmit: null,
      onIdentifierFetched: null,
      onApiRequestFailed: null
    };

    // BACKWARD COMPATIBILITY: Register with global benefits API
    if (typeof window !== 'undefined' && window.benefits && typeof window.benefits._registerWidget === 'function') {
      window.benefits._registerWidget(this);
    }

    // Priority order for auth token:
    // 1. Explicit config.authToken (legacy support)
    // 2. Secure global benefits token (current system)
    this.authToken = config.authToken || this.getSecureAuthToken() || null;

    this.init();
    this.setupInitialAuth().catch(error => {
      logger.error('Authentication initialization failed:', error);
      // Defer to the next event-loop tick so vendor code has a chance to register
      // onError via widget.on('error', ...) before this fires. Without setTimeout the
      // rejection can arrive synchronously before the constructor has returned.
      setTimeout(() => {
        if (this.callbacks.onError) {
          this.callbacks.onError('Authentication initialization failed: ' + error.message);
        }
      }, 0);
    });

    logger.log(` SecureBenefitsWidget created for container: ${containerId}`);
  }

  init() {
    const container = document.getElementById(this.containerId);
    if (!container) {
      const error = `Container with ID "${this.containerId}" not found`;
      logger.error('SecureBenefitsWidget init failed:', error);

      // Trigger error callback
      if (this.callbacks.onError) {
        this.callbacks.onError(error);
      }

      // Try to update status if possible
      try {
        const statusElement = document.getElementById('status');
        if (statusElement) {
          statusElement.textContent = `Error: ${error}`;
        }
      } catch (e) {
        // Ignore if status update fails
      }

      return;
    }

    logger.log('Container found, initializing iframe...');

    this.isReady = false;
    this.iframeLoadCompleted = false;

    // Clear container for iframe
    container.innerHTML = '';

    // Create iframe with security attributes
    this.iframe = document.createElement('iframe');
    // Apply configurable iframe styles with defaults
    const iframeConfig = this.config.iframe || {};
    this.iframe.style.width = iframeConfig.width || '100%';
    this.iframe.style.height = iframeConfig.height || 'auto';
    this.iframe.style.minHeight = iframeConfig.minHeight || '200px';
    this.iframe.style.border = iframeConfig.border || 'none';
    this.iframe.style.overflow = iframeConfig.overflow || 'hidden';

    // Set iframe src asynchronously from backend.
    // The navigation timeout is started here, after src is assigned, so the
    // 10-second budget only counts time the browser spends fetching the page —
    // not the microtask queue drain before getIframeSrcFromBackend() resolves.
    this.getIframeSrcFromBackend().then(src => {
      this.iframe.src = src;
      this.setLoadTimeout();
    }).catch(error => {
      logger.error('Failed to set iframe src:', error);
      this.handleLoadError('Failed to resolve iframe source');
    });

    // allow-same-origin is retained so the iframe can access same-origin cookies and
    // sessionStorage on benefits.mastercard.com for API authentication calls.
    const sandboxPermissions = 'allow-scripts allow-same-origin';

    this.iframe.setAttribute('sandbox', sandboxPermissions);
    //this.iframe.setAttribute('referrerpolicy', 'no-referrer');
    this.iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    this.iframe.setAttribute('loading', 'eager');
    this.iframe.setAttribute('allow', 'encrypted-media');

    // Prevent iframe from being accessed via name
    this.iframe.removeAttribute('name');

    // Listen for messages from iframe (store bound reference for cleanup)
    if (this.boundHandleMessage) {
      window.removeEventListener('message', this.boundHandleMessage);
    }
    this.boundHandleMessage = this.handleMessage.bind(this);
    window.addEventListener('message', this.boundHandleMessage);

    // Enhanced load handling with timeout and retry.
    // Guard against the about:blank load event that browsers fire immediately when an
    // iframe is appended without a src — that premature load would set iframeLoadCompleted
    // and switch to the 30-second readyTimeout before the real URL even starts loading,
    // making the 10-second navigation timeout unreachable.
    this.iframe.addEventListener('load', () => {
      if (!this.iframe.src || this.iframe.src === 'about:blank') {
        return;
      }
      this.iframeLoadCompleted = true;
      this.setLoadTimeout();
      logger.log('benefits-widget: Iframe loaded, waiting for ready signal...');
    });

    // Add error handling for iframe load failures
    this.iframe.addEventListener('error', () => {
      this.handleLoadError('Iframe failed to load');
    });

    container.appendChild(this.iframe);
    // Apply configurable container styles with defaults
    const containerConfig = this.config.container || {};
    container.style.width = containerConfig.width || '100%';
    container.style.height = containerConfig.height || '100%';
    container.style.overflow = containerConfig.overflow || 'hidden';
  }

  setLoadTimeout() {
    // WIDGET_READY already received — the handshake completed. The iframe load event
    // may fire after WIDGET_READY (postMessage is queued at DOMContentLoaded, before
    // all subresources finish), which would restart this timer and destroy a working
    // widget 30 s later. Guard here to prevent that race.
    if (this.isReady) return;
    this.clearLoadTimeout();
    // Once the iframe has loaded, give it the longer readyTimeoutMs budget to finish
    // downloading its own bundle and send WIDGET_READY, instead of reusing the shorter
    // navigation timeout (which was cancelling still-in-flight chunk downloads too early).
    const timeoutDuration = this.iframeLoadCompleted ? this.readyTimeoutMs : this.loadTimeoutMs;
    this.loadTimeout = setTimeout(() => {
      if (this.iframeLoadCompleted) {
        // The iframe HTTP response was received (load event fired, HTTP 200) but
        // WIDGET_READY was never posted. This means the browser blocked the iframe
        // from rendering due to "Content-Security-Policy: frame-ancestors none"
        // returned by the server. Retrying will not change the server's CSP policy,
        // so skip the retry loop and show the configurable error message immediately.
        logger.warn('benefits-widget: Iframe received response but widget did not initialize. Likely blocked by Content-Security-Policy: frame-ancestors none. Showing error without retry.');

        if (this.iframe && this.iframe.parentNode) {
          this.iframe.parentNode.removeChild(this.iframe);
        }

        this.renderFrontendError();

        if (this.callbacks.onError) {
          this.callbacks.onError('Widget failed to load: Iframe source is blocked by Content-Security-Policy frame-ancestors directive.');
        }
      } else {
        // Iframe never even loaded — could be a network error, DNS failure, etc.
        // Use the existing retry logic for these transient failures.
        this.handleLoadError('Iframe load timeout');
      }
    }, timeoutDuration);
  }

  clearLoadTimeout() {
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
    }
  }

  handleLoadError(error) {
    this.clearLoadTimeout();
    logger.error(`Widget load error (attempt ${this.retryCount + 1}/${this.maxRetries}):`, error);

    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      logger.log(`Retrying widget load... (${this.retryCount}/${this.maxRetries})`);

      // Remove failed iframe
      if (this.iframe && this.iframe.parentNode) {
        this.iframe.parentNode.removeChild(this.iframe);
      }

      // Retry after a delay
      setTimeout(() => {
        this.init();
      }, 1000 * this.retryCount); // Exponential backoff
    } else {
      // Max retries reached
      const errorMsg = `Widget failed to load after ${this.maxRetries} attempts: ${error}`;
      logger.error(errorMsg);
      this.renderFrontendError();
      if (this.callbacks.onError) {
        this.callbacks.onError(errorMsg);
      }
    }
  }

  getIframeErrorMessage() {
    return this.config?.iframe?.errorMessage
      || 'We’re having trouble loading information. Please check your connection and try again, or contact support if the issue persists.';
  }

  renderFrontendError() {
    const container = document.getElementById(this.containerId);

    if (!container) {
      return;
    }

    const message = this.getIframeErrorMessage();
    console.error('[SecureBenefitsWidget] IFRAME_LOAD_FAILURE:', message);

    const errorStyles = this.config?.error || {};
    const themeStyles = this.config?.theme || {};
    const errorElement = document.createElement('div');

    errorElement.setAttribute('role', 'alert');
    errorElement.className = 'benefits-widget-iframe-error';
    errorElement.textContent = message;

    errorElement.style.color = errorStyles.color || '#b00020';
    errorElement.style.marginTop = errorStyles.marginTop || '12px';
    errorElement.style.fontSize = errorStyles.fontSize || '14px';
    errorElement.style.textAlign = errorStyles.textAlign || 'center';
    errorElement.style.fontFamily = this.config?.layout?.fontFamily || 'Arial, sans-serif';
    errorElement.style.lineHeight = '1.5';
    errorElement.style.padding = '12px 16px';
    errorElement.style.backgroundColor = themeStyles.backgroundColor || '#fff5f5';
    errorElement.style.border = `1px solid ${themeStyles.borderColor || '#f5c2c7'}`;
    errorElement.style.borderRadius = themeStyles.borderRadius || '6px';
    errorElement.style.boxSizing = 'border-box';
    errorElement.style.width = '100%';

    container.innerHTML = '';
    container.appendChild(errorElement);
  }

  async getIframeSrcFromBackend() {
    const envVars = getEnvVars();
    const baseEndpoint = envVars.IFRAME_URL || `${this.getWidgetDomain()}/services/benefits/iframe.html`;
    logger.log("apiEndpoint::", baseEndpoint);
    // Append the embedder's origin as a query param so secure-iframe.js can validate
    // which page it should accept postMessages from, preventing origin spoofing by a
    // malicious third-party embedder (vendor.com vs evil.com).
    try {
      const url = new URL(baseEndpoint);
      url.searchParams.set('parentOrigin', window.location.origin);
      url.searchParams.set('widgetId', this._widgetId);
      return url.toString();
    } catch (e) {
      return baseEndpoint;
    }
  }

  handleMessage(event) {
    logger.log('benefits-widget: Received message:', event.data);

    // ⚠️ CRITICAL SECURITY VALIDATIONS ⚠️

    // 1. Validate event exists and has data
    if (!event || !event.data) {
      logger.warn('Ignored empty message event');
      return;
    }

    // 2. Validate origin (messages should come FROM our secure iframe)
    // SECURITY: Only accept messages from our own iframe source
    const expectedIframeDomain = this.getExpectedIframeDomain();
    if (!this.isValidIframeOrigin(event.origin, expectedIframeDomain)) {
      logger.warn(`Ignored message from unauthorized origin: ${event.origin}. Expected from: ${expectedIframeDomain}`);
      return;
    }

    // 3. Validate message structure
    if (!event.data || typeof event.data !== 'object') {
      logger.warn('Ignored malformed message');
      return;
    }

    // 4. Validate message source (re-enabled for security)
    if (event.data.source !== 'benefits-widget-iframe') {
      logger.warn(`Ignored message without proper source identifier. Expected: "benefits-widget-iframe", Got: ${event.data.source}`);
      return;
    }

    // 4b. Validate widgetId so two widget instances on the same page don't cross-fire.
    // If the iframe echoes a widgetId and it doesn't match this instance, the message
    // belongs to a sibling widget — silently drop it (no warn, it's expected traffic).
    if (event.data.widgetId && event.data.widgetId !== this._widgetId) {
      return;
    }

    // 5. Validate message type
    const validTypes = [
      'WIDGET_READY', 'BENEFIT_SEARCH_RESULT', 'ERROR',
      'TOKEN_REFRESH_REQUEST', 'TOKEN_REFRESHED', 'TOKEN_REFRESH_ERROR',
      'HEALTH_CHECK_RESPONSE', 'IDENTIFIER_FETCHED', 'API_REQUEST_FAILED', 'SUBMIT_BUTTON_CLICKED',
      'RESIZE_IFRAME', 'IFRAME_READY'
    ];

    if (!validTypes.includes(event.data.type)) {
      logger.warn(`Ignored message with invalid type: ${event.data.type}`);
      return;
    }

    // BACKWARD COMPATIBILITY: Emit benefitDetailsLoaded event on any associated benefits-search element
    const associatedElement = document.querySelector(`benefits-search[data-replaced-by="${this.containerId}"]`);

    switch (event.data.type) {
      case 'WIDGET_READY':
        logger.log('benefits-widget: Widget ready, sending config now...');

        // Send configuration immediately when widget is ready.
        // This also covers re-initialization if the iframe reloads after a retry.
        this.sendMessage({
          type: 'INIT_WIDGET',
          config: this.config,
          authToken: this.authToken
        });

        this.isReady = true;
        this._missedHealthChecks = 0;
        this.clearLoadTimeout();

        // Flush any search that arrived before the widget was ready
        if (this._pendingSearch) {
          this.sendMessage({ type: 'SEARCH_BENEFITS', request: this._pendingSearch });
          this._pendingSearch = null;
        }

        // Log performance metrics
        const initTime = WidgetPerformance.endTiming('widget-initialization');
        const totalLoadTime = Date.now() - this.createdAt;
        logger.log(`Widget ready in ${totalLoadTime}ms (init: ${initTime}ms)`);

        // Start health monitoring
        this.startHealthCheck();

        // Fire onReady only on the first transition — the iframe can reload after a
        // retry and send a second WIDGET_READY; calling onReady twice causes vendor
        // code to re-run setup logic (duplicate listeners, duplicate searches).
        if (!this._wasReady && this.callbacks.onReady) {
          this._wasReady = true;
          this.callbacks.onReady();
        }
        break;

      case 'BENEFIT_SEARCH_RESULT':
        if (this.callbacks.onResult) {
          this.callbacks.onResult(event.data.data);
        }

        if (associatedElement) {
          const benefitDetailsLoadedEvent = new CustomEvent('benefitDetailsLoaded', {
            detail: event.data.result || event.data.data,
            bubbles: true,
            cancelable: true
          });
          associatedElement.dispatchEvent(benefitDetailsLoadedEvent);
        }
        break;

      case 'IDENTIFIER_FETCHED':
        if (this.callbacks.onIdentifierFetched) {
          this.callbacks.onIdentifierFetched(event.data.data);
        }

        if (associatedElement) {
          const cardIdentifierGeneratedEvent = new CustomEvent('cardIdentifierGenerated', {
            detail: event.data.identifier || event.data.data,
            bubbles: true,
            cancelable: true
          });
          associatedElement.dispatchEvent(cardIdentifierGeneratedEvent);
        }
        break;

      case 'API_REQUEST_FAILED':
        if (this.callbacks.onApiRequestFailed) {
          this.callbacks.onApiRequestFailed(event.data);
        }

        if (associatedElement) {
          const apiRequestFailedEvent = new CustomEvent('apiRequestFailed', {
            detail: event.data,
            bubbles: true,
            cancelable: true
          });
          associatedElement.dispatchEvent(apiRequestFailedEvent);
          logger.log('apiRequestFailedEvent:', apiRequestFailedEvent);
        }
        break;

      case 'SUBMIT_BUTTON_CLICKED':
        if (this.callbacks.onSubmit) {
          this.callbacks.onSubmit(event.data.detail);
        }

        if (associatedElement) {
          const requestSubmittedEvent = new CustomEvent('requestSubmitted', {
            detail: { message: 'Submit button clicked' },
            bubbles: true,
            cancelable: true
          });
          associatedElement.dispatchEvent(requestSubmittedEvent);
        }
        break;

      case 'ERROR':
        if (this.callbacks.onError) {
          this.callbacks.onError(event.data.error);
        }
        break;

      case 'TOKEN_REFRESH_REQUEST':
        this.handleTokenRefreshRequest();
        break;

      case 'TOKEN_REFRESHED':
        if (this.callbacks.onTokenRefresh) {
          this.callbacks.onTokenRefresh(event.data.token);
        }
        break;

      case 'TOKEN_REFRESH_ERROR':
        logger.error('Token refresh failed:', event.data.error);
        if (this.callbacks.onError) {
          this.callbacks.onError('Token refresh failed: ' + event.data.error);
        }
        break;

      case 'HEALTH_CHECK_RESPONSE':
        logger.log('Health check response:', event.data.status);
        this._missedHealthChecks = 0;
        break;

      case 'RESIZE_IFRAME':
        // Auto-resize iframe based on content height. Clamped between the configured
        // minimum and 2000 px to prevent a bug or spoofed message from breaking the
        // vendor page layout with an arbitrarily large iframe.
        if (event.data.height && this.iframe) {
          const minH = parseInt(this.iframe.style.minHeight) || 200;
          const newHeight = Math.min(Math.max(event.data.height, minH), 2000);
          this.iframe.style.height = newHeight + 'px';
          logger.log(`Iframe resized to ${newHeight}px`);
        }
        break;

      case 'IFRAME_READY':
        logger.log('benefits-widget: Iframe script loaded and executing');
        break;
    }
  }

  sendMessage(message) {
    if (this.iframe && this.iframe.contentWindow) {
      message.source = 'benefits-widget-parent';
      logger.log('benefits-widget: Sending message to iframe:', message);

      // SECURITY FIX: Use specific target origin instead of '*'
      const targetOrigin = this.getExpectedIframeDomain();
      this.iframe.contentWindow.postMessage(message, targetOrigin);
    } else {
      logger.error('benefits-widget: Cannot send message - iframe not ready:', {
        iframe: !!this.iframe,
        contentWindow: !!this.iframe?.contentWindow
      });
    }
  }

  // Public API methods
  onResult(callback) {
    this.callbacks.onResult = callback;
    return this;
  }

  onReady(callback) {
    this.callbacks.onReady = callback;
    return this;
  }

  onError(callback) {
    this.callbacks.onError = callback;
    return this;
  }

  onSubmit(callback) {
    this.callbacks.onSubmit = callback;
    return this;
  }

  onIdentifierFetched(callback) {
    this.callbacks.onIdentifierFetched = callback;
    return this;
  }

  onApiRequestFailed(callback) {
    this.callbacks.onApiRequestFailed = callback;
    return this;
  }

  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    if (this.isReady) {
      this.sendMessage({
        type: 'UPDATE_CONFIG',
        config: this.config
      });
    }
  }

  searchBenefits(request) {
    if (this.isReady) {
      this.sendMessage({
        type: 'SEARCH_BENEFITS',
        request: request
      });
    } else {
      logger.log('Widget not ready yet, queuing search request.');
      this._pendingSearch = request;
    }
  }

  // Health Check and Connection Monitoring
  startHealthCheck() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }

    this.healthCheckInterval = setInterval(() => {
      if (this.isReady && this.iframe) {
        this._missedHealthChecks++;
        // Three consecutive missed responses (90 s) means the iframe is unresponsive.
        // Mark not-ready and reload so the widget self-heals instead of sitting silently broken.
        if (this._missedHealthChecks >= 3) {
          logger.warn(`benefits-widget: ${this._missedHealthChecks} consecutive health checks missed — reloading iframe`);
          this.isReady = false;
          this._wasReady = false; // allow onReady to fire again after recovery
          this._missedHealthChecks = 0;
          this.retryCount = 0;
          this.stopHealthCheck();
          if (this.iframe && this.iframe.parentNode) {
            this.iframe.parentNode.removeChild(this.iframe);
          }
          this.init();
          return;
        }
        this.sendMessage({
          type: 'HEALTH_CHECK',
          timestamp: Date.now()
        });
      }
    }, 30000); // Check every 30 seconds
  }

  stopHealthCheck() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }

  // Get widget status information for debugging
  getStatus() {
    return {
      isReady: this.isReady,
      hasAuthToken: !!this.authToken,
      retryCount: this.retryCount,
      containerId: this.containerId,
      iframeLoaded: !!(this.iframe && this.iframe.contentWindow),
      lastHealthCheck: this.lastHealthCheck || null
    };
  }

  destroy() {
    // Enhanced cleanup
    try {
      // Send destroy message to iframe
      if (this.isReady) {
        this.sendMessage({ type: 'DESTROY_WIDGET' });
      }

      // Clear all timeouts and intervals
      this.clearLoadTimeout();
      this.stopHealthCheck();

      // Remove iframe
      if (this.iframe && this.iframe.parentNode) {
        this.iframe.parentNode.removeChild(this.iframe);
      }

      // Clean up message listener
      if (this.boundHandleMessage) {
        window.removeEventListener('message', this.boundHandleMessage);
      }

      // Clean up blob URL if created
      if (this._blobUrl) {
        URL.revokeObjectURL(this._blobUrl);
        this._blobUrl = null;
      }

      // Unregister from global benefits API
      if (typeof window !== 'undefined' && window.benefits && typeof window.benefits._unregisterWidget === 'function') {
        window.benefits._unregisterWidget(this);
      }

      // Clear references
      this.iframe = null;
      this.isReady = false;
      this._wasReady = false;
      this._missedHealthChecks = 0;
      this.authToken = null;
      this.callbacks = {};
      this.loadTimeout = null;
      this.healthCheckInterval = null;
      this._pendingSearch = null;

      logger.log('Widget destroyed successfully');
    } catch (error) {
      logger.error('Error during widget destruction:', error);
    }
  }

  // Authentication token management methods
  async setupInitialAuth() {
    // Initialize token from various sources for initial setup
    await this.initializeToken();

    // Note: The widget itself handles periodic token refresh
    // This is just for initial authentication setup
  }

  async initializeToken() {
    // Try to get initial token from multiple sources.
    // Priority must match the order at construction time (line ~192) so that a fresh
    // token from benefits.init() is never overwritten by a stale sessionStorage value.

    // 1. From manual config (provided by third-party site)
    if (this.config.authToken) {
      this.authToken = this.config.authToken;
      return;
    }

    // 2. From global secure API — benefits.init() stores it here
    const secureToken = this.getSecureAuthToken();
    if (secureToken) {
      this.authToken = secureToken;
      return;
    }

    // 3. From sessionStorage (persisted from a previous session on this page)
    const storedToken = sessionStorage.getItem('X-Benefits-Auth-Token');
    if (storedToken) {
      this.authToken = storedToken;
      return;
    }

    // 4. From regular cookie (not httpOnly - set by third-party site)
    const cookieToken = this.getCookieValue('X-Benefits-Auth-Token');
    if (cookieToken) {
      this.authToken = cookieToken;
      sessionStorage.setItem('X-Benefits-Auth-Token', cookieToken);
      return;
    }
  }

  // Helper method to get cookie value (for regular cookies, NOT httpOnly)
  getCookieValue(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  // SECURITY: Get widget's own domain (not parent site's domain)
  getWidgetDomain() {
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      return 'http://localhost:8080';
    }
    // Derive origin from the resolved BASE_URL env var (set at build/deploy time).
    const envVars = getEnvVars();
    if (envVars.BASE_URL) {
      try { return new URL(envVars.BASE_URL).origin; } catch (e) {}
    }
    // Fall back to the origin of the loaded widget script itself so that the widget
    // always resolves to its actual deployed domain regardless of environment.
    if (typeof document !== 'undefined') {
      const script = Array.from(document.querySelectorAll('script[src]'))
        .find(s => /benefits-widget/.test(s.src));
      if (script) {
        try { return new URL(script.src).origin; } catch (e) {}
      }
    }
    return '';
  }

  // SECURITY: Get expected iframe domain for origin validation.
  // Must use the same URL source as getIframeSrcFromBackend() (IFRAME_URL env var),
  // not getIframeSrc() (BASE_URL env var), otherwise origin validation rejects all
  // messages from the real iframe when the two env vars resolve to different hostnames.
  getExpectedIframeDomain() {
    try {
      const envVars = getEnvVars();
      const iframeSrc = envVars.IFRAME_URL || `${this.getWidgetDomain()}/services/benefits/iframe.html`;
      return new URL(iframeSrc).origin;
    } catch (error) {
      logger.warn('Failed to parse iframe domain:', error);
      return this.getWidgetDomain();
    }
  }

  // SECURITY: Validate that message origin is from our trusted iframe
  isValidIframeOrigin(messageOrigin, expectedDomain) {
    if (messageOrigin === expectedDomain) {
      return true;
    }

    // Exact-origin allowlist. substring/includes checks are intentionally avoided
    // because they allow spoofing via domains like
    // "evil-stage.benefits.mastercard.com.attacker.com".
    const masterCardDomains = [
      'benefits.mastercard.com',
      'stage.benefits.mastercard.com',
      'mtf.benefits.mastercard.com',
      'dev.benefits.mastercard.com',
    ];

    if (masterCardDomains.some(domain =>
      messageOrigin === `https://${domain}` || messageOrigin === `http://${domain}`
    )) {
      return true;
    }

    // Development only
    return messageOrigin === 'http://localhost:8080' || messageOrigin === 'https://localhost:8080';
  }

  // SECURITY: Secure method to get auth token - only for authorized widget instances
  getSecureAuthToken() {
    // Primary: Get token from secure private storage (only for authorized widgets)
    if (typeof window !== 'undefined' &&
      window.benefits &&
      typeof window.benefits._getSecureToken === 'function' &&
      window.benefits._isSecureBenefitsAPI === true) {
      const token = window.benefits._getSecureToken(this);
      if (token) {
        return token;
      }
    }

    // Fallback to other secure sources (for backward compatibility)
    const storedToken = sessionStorage.getItem('X-Benefits-Auth-Token');
    if (storedToken) {
      return storedToken;
    }

    const cookieToken = this.getCookieValue('X-Benefits-Auth-Token');
    if (cookieToken) {
      return cookieToken;
    }

    return null;
  }

  handleTokenRefreshRequest() {
    // Widget is requesting token refresh assistance from parent
    // This is optional - widget handles its own refresh, but parent can assist
    if (this.callbacks.onTokenRefresh) {
      // Let the parent application handle the refresh if needed
      this.callbacks.onTokenRefresh(null, true); // true indicates refresh request
    }
  }

  updateTokenInIframe(token) {
    this.sendMessage({
      type: 'UPDATE_AUTH_TOKEN',
      token: token
    });
  }

  // Public API for third-party sites to provide authentication token
  setAuthToken(token) {
    if (!token || typeof token !== 'string') {
      throw new Error('Auth token must be a non-empty string');
    }

    this.authToken = token;
    sessionStorage.setItem('X-Benefits-Auth-Token', token);

    // Token is delivered to the iframe via the INIT_WIDGET postMessage below.
    // A cookie cannot be used here: this code runs on the embedder's domain (e.g.
    // vendor.com) and the iframe is at a different origin (benefits.mastercard.com),
    // so same-origin policy prevents the iframe from reading the embedder's cookies.

    if (this.isReady) {
      this.updateTokenInIframe(token);
    }

    return this;
  }

  getAuthToken() {
    return this.authToken;
  }

  onTokenRefresh(callback) {
    this.callbacks.onTokenRefresh = callback;
    return this;
  }

  forceTokenRefresh() {
    // Send message to widget to force refresh
    this.sendMessage({
      type: 'FORCE_TOKEN_REFRESH'
    });
    return this;
  }
}

// Make it available globally
window.SecureBenefitsWidget = SecureBenefitsWidget;

// (benefits object already defined at the top of the file)

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SecureBenefitsWidget;
}

// Helper function to configure Stencil component
function configureStencilComponent(element, config) {
  logger.log('Setting Stencil component data:', config);

  // Set the data property directly on the Stencil component
  element.data = JSON.stringify(config);

  // Also set the attribute for fallback
  element.setAttribute('data', JSON.stringify(config));

  // Trigger a re-render if the component has a method for it
  if (element.forceUpdate && typeof element.forceUpdate === 'function') {
    element.forceUpdate();
  }

  // Set up event listeners for backward compatibility
  element.addEventListener('benefitResult', (event) => {
    logger.log('Declarative widget result:', event.detail);
    // Emit the original 'benefitDetailsLoaded' event for backward compatibility
    const customEvent = new CustomEvent('benefitDetailsLoaded', {
      detail: event.detail,
      bubbles: true,
      cancelable: true
    });
    element.dispatchEvent(customEvent);
  });

  // Also listen for the actual benefitDetailsLoaded event from Stencil component
  element.addEventListener('benefitDetailsLoaded', (event) => {
    logger.log('Declarative widget benefitDetailsLoaded:', event.detail);
    // Re-emit on document for third-party sites that use document.querySelector
    const documentEvent = new CustomEvent('benefitDetailsLoaded', {
      detail: event.detail,
      bubbles: true,
      cancelable: true
    });
    document.dispatchEvent(documentEvent);
  });

  // Also listen for the actual cardIdentifierGenerated event from Stencil component
  element.addEventListener('cardIdentifierGenerated', (event) => {
    logger.log('Declarative widget cardIdentifierGenerated:', event.detail);
    // Re-emit on document for third-party sites that use document.querySelector
    const documentEvent = new CustomEvent('cardIdentifierGenerated', {
      detail: event.detail,
      bubbles: true,
      cancelable: true
    });
    document.dispatchEvent(documentEvent);
  });

  // Also listen for the actual apiRequestFailed event from Stencil component
  element.addEventListener('apiRequestFailed', (event) => {
    logger.log('Declarative widget apiRequestFailed:', event.detail);
    logger.log('apiRequestFailed event received in benefits-widget.js:', event); // Log the entire event object
    logger.log('apiRequestFailed event detail in benefits-widget.js:', event.detail); // Check if event.detail has data or is undefined
    // Re-emit on document for third-party sites that use document.querySelector
    const documentEvent = new CustomEvent('apiRequestFailed', {
      detail: event.detail,
      bubbles: true,
      cancelable: true
    });
    document.dispatchEvent(documentEvent);
  });

  // Also listen for the actual requestSubmitted event from Stencil component
  element.addEventListener('requestSubmitted', (event) => {
    logger.log('Declarative widget requestSubmitted:', event.detail);
    // Re-emit on document for third-party sites that use document.querySelector
    const documentEvent = new CustomEvent('requestSubmitted', {
      detail: event.detail,
      bubbles: true,
      cancelable: true
    });
    document.dispatchEvent(documentEvent);
  });

  // Mark as initialized
  element.setAttribute('data-initialized', 'true');
}

// MutationObserver to watch for new <benefits-search> elements
function watchBenefitsSearchElements() {
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === 1 && node.tagName.toLowerCase() === 'benefits-search') {
          if (!node.__initialized) {
            logger.debug('[SecureBenefitsWidget] Initializing dynamically added <benefits-search> element');
            initSingleBenefitsSearchElement(node);
          }
        }
        // Also check for nested <benefits-search> elements
        if (node.nodeType === 1 && node.querySelectorAll) {
          const nested = node.querySelectorAll('benefits-search');
          if (nested && nested.length) {
            nested.forEach(el => {
              if (!el.__initialized) {
                logger.debug('[SecureBenefitsWidget] Initializing nested <benefits-search> element');
                initSingleBenefitsSearchElement(el);
              }
            });
          }
        }
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

// Helper function to initialize a single benefits-search element
function initSingleBenefitsSearchElement(element) {
  try {
    // Parse the JSON configuration from the data attribute
    const dataAttr = element.getAttribute('data');
    let config = {};

    if (dataAttr) {
      try {
        config = JSON.parse(dataAttr);
      } catch (error) {
        logger.error('Invalid JSON in benefits-search data attribute:', error);
        return;
      }
    }

    // Mark as initialized to prevent duplicate processing
    element.__initialized = true;

    // Create a unique container ID for this declarative widget
    const containerId = `benefits-widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Create a container div but keep the benefits-search element for backward compatibility
    const container = document.createElement('div');
    container.id = containerId;
    container.className = 'benefits-widget-container';
    container.style.cssText = element.style.cssText; // Preserve any existing styles

    // Insert the container after the benefits-search element
    element.parentNode.insertBefore(container, element.nextSibling);

    // Hide the original benefits-search element but keep it in DOM for event listeners
    element.style.display = 'none';
    element.setAttribute('data-replaced-by', containerId);

    // Create the secure widget instance
    const widget = new SecureBenefitsWidget(containerId, config);

    logger.log('✅ Initialized benefits-search element with config:', config);
  } catch (error) {
    logger.error('Error initializing benefits-search element:', error);
  }
}

// BACKWARD COMPATIBILITY: Auto-initialize declarative elements
function initDeclarativeWidgets() {
  logger.log('initDeclarativeWidgets running');
  // Initialize any declarative benefits-search element; data config remains optional.
  const benefitsSearchElements = document.querySelectorAll('benefits-search');
  logger.log('Found', benefitsSearchElements.length, 'benefits-search elements');

  benefitsSearchElements.forEach((element, index) => {
    if (!element.__initialized) {
      initSingleBenefitsSearchElement(element);
    }
  });
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initDeclarativeWidgets();
    watchBenefitsSearchElements();
  });
} else {
  // DOM is already ready
  initDeclarativeWidgets();
  watchBenefitsSearchElements();
}

//  Mark benefits API as ready and process any queued init calls
if (typeof window !== 'undefined' && window.benefits && typeof window.benefits._setReady === 'function') {
  window.benefits._setReady();
}

// ENSURE GLOBAL AVAILABILITY: Set SecureBenefitsWidget on window after everything is loaded
if (typeof window !== 'undefined') {
  window.SecureBenefitsWidget = SecureBenefitsWidget;
  logger.log('✅ SecureBenefitsWidget globally available:', typeof window.SecureBenefitsWidget);
}
